import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Button,
  Checkbox,
  Input,
  Label,
  Textarea,
  Avatar,
  AvatarImage,
  AvatarFallback,
  FormSelect,
  Paragraph,
  WysiwygEditor,
} from '@/components/atoms';
import { FileUpload, FormField } from '@/components/molecules';
import { cn } from '@/lib/utils';
import {
  storeCreationSchema,
  type StoreCreationFormData,
} from '@/validation-schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import AddPhotoIcon from '@/assets/svg/add-photo.svg?react';
import { useQueryState } from 'nuqs';
import {
  useStore,
  useCreateStore,
  useUpdateStore,
  uploadFile,
  useCheckDomain,
} from '@/api';
import { useNavigate } from 'react-router';
import { routes, businessIndustries, DOMAIN_REGEX } from '@/lib/constants';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { useDebounce } from '@/hooks/use-debounce';
import { CustomDomainDialog } from './custom-domain-dialog';

export const StoreBuilderForm = () => {
  const [showRefund, setShowRefund] = useState(true);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [removeCover, setRemoveCover] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [customDomainOpen, setCustomDomainOpen] = useState(false);

  const [id] = useQueryState('id');
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, dirtyFields },
    setValue,
    watch,
    control,
  } = useForm({
    resolver: yupResolver(storeCreationSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const industryValue = watch('industry');
  const domainValue = watch('domain') || '';

  const { data: store } = useStore(id ? String(id) : undefined);

  const isDomainPatternValid = DOMAIN_REGEX.test(domainValue);
  const isOriginalDomain = domainValue === store?.domain;
  const shouldCheckDomain =
    domainValue.length > 0 && isDomainPatternValid && !isOriginalDomain;

  const debouncedDomain = useDebounce(
    shouldCheckDomain ? domainValue.trim().toLowerCase() : '',
    500
  );

  const isTypingDomain =
    domainValue.trim().toLowerCase() !== debouncedDomain && shouldCheckDomain;

  const {
    data: domainCheck,
    isLoading: isCheckingDomain,
    isError: isDomainCheckError,
  } = useCheckDomain(debouncedDomain);

  useEffect(() => {
    if (store) {
      setValue('name', store.name);
      setValue('domain', store.domain);
      setValue('industry', store.industry);
      setValue('description', store.description || '');
      setShowRefund(!!store.refundPolicy);
      setValue('refundPolicy', store.refundPolicy || '');
      setValue('instagram', store.socialLinks?.instagram || '');
      setValue('whatsapp', store.socialLinks?.whatsapp || '');
      setValue('tiktok', store.socialLinks?.tiktok || '');
      setRemoveLogo(false);
      setRemoveCover(false);
      setAvatarFile(null);
      setCoverFile(null);
    }
  }, [store, setValue]);

  const { mutate, isPending: isCreationPending } = useCreateStore((data) => {
    setIsLoading(false);
    const createdId = data.data?.id;
    if (createdId) {
      navigate(`${routes.storeBuilder}?id=${createdId}`, { replace: true });
      return;
    }
    navigate(routes.store);
  });

  const { mutate: modify, isPending: isModificationPending } = useUpdateStore(
    () => {
      setIsLoading(false);
      navigate(routes.store);
    }
  );

  const avatarUrl = useMemo(
    () =>
      avatarFile
        ? URL.createObjectURL(avatarFile)
        : !removeLogo
          ? store?.logo?.url
          : undefined,
    [avatarFile, removeLogo, store?.logo?.url]
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setAvatarFile(file);
    setRemoveLogo(false);
  };

  const create = async (storeData: StoreCreationFormData) => {
    let avatarId: number | undefined;
    let coverId: number | undefined;

    setIsLoading(true);

    try {
      const { instagram, whatsapp, tiktok, ...rest } = storeData;

      const trimmedInstagram = instagram?.trim() || '';
      const trimmedWhatsapp = whatsapp?.trim() || '';
      const trimmedTiktok = tiktok?.trim() || '';

      const socialLinks =
        trimmedInstagram || trimmedWhatsapp || trimmedTiktok
          ? {
              instagram: trimmedInstagram || undefined,
              whatsapp: trimmedWhatsapp || undefined,
              tiktok: trimmedTiktok || undefined,
            }
          : undefined;

      const payload = {
        ...rest,
        refundPolicy: showRefund ? storeData.refundPolicy : '',
        ...(socialLinks ? { socialLinks } : {}),
      };

      if (avatarFile) {
        const formData = new FormData();
        formData.append('file', avatarFile);
        const res = await uploadFile(formData);
        avatarId = res.data[0].id;
      }

      if (coverFile) {
        const formData = new FormData();
        formData.append('file', coverFile);
        const res = await uploadFile(formData);
        coverId = res.data[0].id;
      }

      const basePayload = {
        ...payload,
        ...(avatarId && { logoId: avatarId }),
        ...(coverId && { coverId }),
      };

      const finalPayload = {
        ...basePayload,
        ...(isEditMode && removeLogo ? { logoId: undefined } : {}),
        ...(isEditMode && removeCover ? { coverId: undefined } : {}),
      };

      if (!id) {
        mutate({
          ...finalPayload,
        });
      } else {
        modify({
          id,
          ...finalPayload,
        });
      }
    } catch {
      toast.error('Failed to upload files');
      setIsLoading(false);
    }
  };

  const isEditMode = !!id;

  // Form changes
  const hasFormChanges = Object.keys(dirtyFields).length > 0;

  // File changes (this is the key requirement)
  const hasFileChanges = !!avatarFile || !!coverFile;

  // Any change at all
  const hasChanges = hasFormChanges || hasFileChanges;

  // Asset requirement (create only)
  const assetsValid = true;

  // Domain validation (only if edited)
  const domainValid =
    !shouldCheckDomain ||
    (domainCheck?.available && !isTypingDomain && !isCheckingDomain);

  return (
    <div className="grid grid-cols-3 gap-6 lg:gap-8 lg:px-8">
      <div className="col-span-3 lg:col-span-1 flex items-center flex-col gap-8">
        <div className="flex flex-col gap-2 items-center">
          <Avatar
            className="size-20 aspect-square cursor-pointer"
            onClick={handleAvatarClick}
          >
            <AvatarImage
              alt="store logo"
              className="object-cover rounded-full"
              src={avatarUrl || store?.logo?.url || undefined}
            />
            <AvatarFallback className="text-xl">
              <AddPhotoIcon />
            </AvatarFallback>
          </Avatar>
          <Input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
          <span className="text-sm">Upload store logo</span>
          {(avatarFile || store?.logo?.url) && (
            <Button
              variant="link"
              type="button"
              className="mt-1 text-destructive text-xs h-fit"
              label="Remove logo"
              onClick={() => {
                setAvatarFile(null);
                setRemoveLogo(true);
              }}
            />
          )}
        </div>
        <div className="flex flex-col gap-2 items-center w-full">
          <FileUpload
            value={coverFile}
            onChange={setCoverFile}
            existingUrl={!removeCover ? store?.cover?.url : undefined}
          />
          <span className="text-sm">Upload store cover image</span>
        </div>
        <Label className="flex gap-2 cursor-pointer">
          <Checkbox
            checked={showRefund}
            onCheckedChange={(checked) => setShowRefund(!!checked)}
            className="cursor-pointer"
          />
          <span>Refund policy?</span>
        </Label>
      </div>
      <form
        onSubmit={handleSubmit(create)}
        className="col-span-3 lg:col-span-2 grid grid-cols-2 gap-3 h-fit"
      >
        <FormField
          label="Store name"
          className="col-span-2 lg:col-span-1"
          error={errors.name?.message}
        >
          <Input placeholder="eg. Ishaya's Store" {...register('name')} />
        </FormField>
        <FormField
          label="Domain name"
          className="col-span-2 lg:col-span-1"
          error={errors.domain?.message}
          infoTip="This is your website address on the internet (e.g. yourbrand.sellyt.shop). It helps customers find your business online."
        >
          <div className="flex flex-col gap-2">
            <div className="relative">
              <Input placeholder="eg. BeautyByMe" {...register('domain')} />
              <div className="absolute bg-gray-200 rounded-sm text-xs py-1.5 px-2 top-1.5 right-1.5 font-medium">
                .sellyt.shop
              </div>
            </div>
            {shouldCheckDomain && (
              <div className="flex items-center gap-2">
                {isCheckingDomain || isTypingDomain ? (
                  <>
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    <Paragraph size="xs" className="text-muted-foreground">
                      Checking availability...
                    </Paragraph>
                  </>
                ) : isDomainCheckError ? (
                  <>
                    <XCircle className="size-4 text-destructive" />
                    <Paragraph size="xs" className="text-destructive">
                      Error checking domain availability
                    </Paragraph>
                  </>
                ) : domainCheck?.available ? (
                  <>
                    <CheckCircle2 className="size-4 text-green-500" />
                    <Paragraph size="xs" className="text-green-500">
                      {debouncedDomain}.sellyt.shop is available
                    </Paragraph>
                  </>
                ) : (
                  <>
                    <XCircle className="size-4 text-destructive" />
                    <Paragraph size="xs" className="text-destructive">
                      {debouncedDomain}.sellyt.shop is not available
                    </Paragraph>
                  </>
                )}
              </div>
            )}
            <button
              type="button"
              className="text-sm text-primary hover:underline w-fit text-left"
              onClick={() => setCustomDomainOpen(true)}
            >
              Use a custom domain instead
            </button>
          </div>
        </FormField>
        <FormField
          label="Industry"
          className="col-span-2 lg:col-span-1"
          error={errors.industry?.message}
        >
          <FormSelect
            placeholder="Select industry"
            value={industryValue}
            onChange={(value) => setValue('industry', value)}
            options={businessIndustries}
          />
        </FormField>
        <FormField
          label="Whatsapp link (optional)"
          className="col-span-2 lg:col-span-1"
          error={errors.whatsapp?.message}
        >
          <Input
            placeholder="eg. https:wa.me/+2348123456789"
            {...register('whatsapp')}
          />
        </FormField>
        <FormField
          label="Instagram link (optional)"
          className="col-span-2 lg:col-span-1"
          error={errors.instagram?.message}
        >
          <Input
            placeholder="eg. https://instagram.com/sellyt"
            {...register('instagram')}
          />
        </FormField>
        <FormField
          label="Tiktok link (optional)"
          className="col-span-2 lg:col-span-1"
          error={errors.tiktok?.message}
        >
          <Input
            placeholder="eg. https://tiktok.com/@sellyt"
            {...register('tiktok')}
          />
        </FormField>
        <FormField
          label="Refund policy (optional)"
          className={cn('col-span-2 lg:col-span-1', { hidden: !showRefund })}
          error={errors.refundPolicy?.message}
        >
          <Textarea
            placeholder="Enter refund policy"
            className="min-h-32 h-full"
            {...register('refundPolicy')}
          />
        </FormField>
        <FormField
          label="Store description"
          className={cn('col-span-2 lg:col-span-1', {
            'lg:col-span-2': !showRefund,
          })}
          error={errors.description?.message}
        >
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <WysiwygEditor
                value={field.value || ''}
                onChange={field.onChange}
                placeholder="Enter store description"
                minHeight={160}
              />
            )}
          />
        </FormField>
        <div className="col-span-2 grid grid-cols-3 gap-2.5 max-md:flex max-md:flex-col-reverse">
          <Button
            variant="outline"
            className="col-span-3 md:col-span-1"
            label="Cancel"
            type="button"
            onClick={() => navigate(routes.store)}
            size="lg"
          />
          <Button
            className="col-span-3 md:col-span-2"
            label={`${!id ? 'Create' : 'Modify'} store`}
            size="lg"
            type="submit"
            isLoading={isCreationPending || isModificationPending || isLoading}
            disabled={
              !assetsValid ||
              !domainValid ||
              !isValid ||
              (isEditMode && !hasChanges)
            }
          />
        </div>
      </form>

      <CustomDomainDialog
        open={customDomainOpen}
        hasStore={Boolean(store?.id)}
        onOpenChange={setCustomDomainOpen}
      />
    </div>
  );
};
